import React from "react";
import {TFunction} from "i18next";
import {useTranslation} from "react-i18next";
import Table, { TableProps } from "@awsui/components-react/table";
import Header from "@awsui/components-react/header";
import Competition from "model/Competition";
import StandingsItem from "model/StandingsItem";
import getStandings from "api/getStandings";
import { useQuery } from "react-query";
import useTable from "hooks/useTable";
import Pagination from "@awsui/components-react/pagination";
import TextFilter from "@awsui/components-react/text-filter";
import CollectionPreferences from "@awsui/components-react/collection-preferences";
import { useAuth } from "hooks/useAuth";
import { formatNumber } from "common/formatNumber";
import Box from "@awsui/components-react/box";
import PositionIcon from "../../../components/icons/PositionIcon";


interface StandingsTableProps {
    competition: Competition;
}

const buildColumnDefinitions = (
    t: TFunction,
    tCommon: TFunction,
    competition: Competition,
    standings: StandingsItem[] | undefined
): TableProps.ColumnDefinition<StandingsItem>[] => [
    {
        id: "position",
        header: t("columns.position"),
        cell: (item: StandingsItem) => item.position || "-",
        sortingField: "position"
    },
    {
        id: "username",
        header: t("columns.user"),
        cell: (item: StandingsItem) =>
            <Box variant="span">
                <PositionIcon competition={competition} length={standings?.length} position={item.position} />
                {item.user || "-"}
            </Box>,
        sortingField: "user"
    },
    {
        id: "points",
        header: t("columns.totalPoints"),
        cell: (item: StandingsItem) => {
            const penaltyPoints = item.penaltyPoints || 0;
            if (penaltyPoints > 0) {
                return (
                    <Box variant="span">
                        {item.points || "-"} <span style={{ color: "red" }}>(-{penaltyPoints})</span>
                    </Box>
                );
            }
            return item.points || "-";
        },
        sortingField: "points"
    },
    {
        id: "value",
        header: t("columns.lineupValue"),
        cell: (item: StandingsItem) => {
            const value = `${formatNumber(item.value, 1)} ${tCommon("common.million")}`;
            // The backend only sends completeLineup=false for users with 1-11 picks
            // (users with zero picks aren't in the response at all). Testing for an
            // explicit `false` means a frontend deployed ahead of the backend simply
            // shows no marker, rather than flagging every single user.
            if (item.completeLineup === false) {
                return (
                    <Box variant="span">
                        {value} <Box variant="span" color="text-body-secondary">({t("missingRiders")})</Box>
                    </Box>
                );
            }
            return value;
        },
        sortingField: "value"
    },
    {
        id: "lineup_points",
        header: t("columns.lineupPoints"),
        cell: (item: StandingsItem) => item.lineupPoints || "-",
        sortingField: "lineupPoints",
    },
    {
        id: "questions_points",
        header: t("columns.questionsPoints"),
        cell: (item: StandingsItem) => (item.answeredQuestions) ? item.questionsPoints || "-" : tCommon("common.notAnswered"),
        sortingField: "questionsPoints",
    }
];

const StandingsTable: React.FC<StandingsTableProps> = (props) => {

    const {t} = useTranslation("standings");
    const {t: tCommon} = useTranslation("common");
    const competition = props.competition;

    const [{ league, user }] = useAuth();

    // <LeagueRoute> wraps /standings in App.tsx, so league is always defined here.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const leagueForApi = league!;

    const {
        isLoading: standingsIsLoading,
        data: standings,
        error: getStandingsError
    } = useQuery<StandingsItem[], Error>(`getStandings-${competition.code}`, () =>
        getStandings(competition, leagueForApi, user?.token)
    );

    const {
        preferences,
        setPreferences,
        items,
        collectionProps,
        filterProps,
        paginationProps,
        collectionPreferencesProps
    } = useTable(standings || [], {
        emptyStateSubtitle: getStandingsError as Error,
        emptyStateTitle: t("empty"),
        pageSize: 10
    });

    return (
        <Table
            {...collectionProps}
            loadingText={t("loading")}
            columnDefinitions={buildColumnDefinitions(t, tCommon, competition, standings)}
            items={items}
            loading={standingsIsLoading}
            wrapLines={false}
            header={<Header>{t("title")}</Header>}
            pagination={
                <Pagination
                    {...paginationProps}
                    ariaLabels={{
                        nextPageLabel: tCommon("table.nextPage"),
                        previousPageLabel: tCommon("table.previousPage"),
                        pageLabel: pageNumber => tCommon("table.page", {page: pageNumber})
                    }}
                />
            }
            filter={
                <TextFilter
                    {...filterProps}
                    filteringPlaceholder={tCommon("table.filterPlaceholder")}
                />
            }
            preferences={
                <CollectionPreferences
                    {...collectionPreferencesProps}
                    preferences={preferences}
                    onConfirm={({ detail }) => setPreferences(detail)}
                />
            }
        />
    );
};

export default StandingsTable;
